import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import ConfirmationModal from './ui/ConfirmationModal';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { parseShotFilter } from '../utils/shotFilterParse';
import { PIPELINE_SPECIALIZATIONS } from '../constants/biddingPipeline';

interface Project {
  id: string;
  name: string;
  currency?: string;
}

interface BiddingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bidding: any) => void;
  projects: Project[];
  currentUserId?: string;
  biddingToEdit?: any;
  mode?: 'video' | 'manual';
  selectedProject?: Project;
}

const BiddingModal: React.FC<BiddingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  projects,
  currentUserId,
  biddingToEdit,
  mode = 'video',
  selectedProject
}) => {
  const { t } = useTranslation();
  
  const [name, setName] = useState(biddingToEdit?.name || '');
  const [projectId, setProjectId] = useState(biddingToEdit?.projectId || '');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [shotCount, setShotCount] = useState<number>(10);
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [progressThumbnails, setProgressThumbnails] = useState<string[]>([]);
  const [progressShotNames, setProgressShotNames] = useState<(string | null)[]>([]);
  const [detectionProgress, setDetectionProgress] = useState<{
    percent?: number;
    matchedCount?: number;
    filterTotal?: number;
    playhead?: number;
    phase?: string;
  } | null>(null);
  const [shotFilterText, setShotFilterText] = useState('');
  const [insertMissingShots, setInsertMissingShots] = useState(false);
  const [showSpecsWarning, setShowSpecsWarning] = useState(false);

  const parsedShotFilter = useMemo(() => parseShotFilter(shotFilterText), [shotFilterText]);

  const currentProject = projects.find(p => p.id === projectId);
  const currency = currentProject?.currency || 'USD';

  // Track if auto-fill has been done for current modal session
  const hasAutoFilledRef = useRef(false);

  // Auto-fill when modal opens with selectedProject (only once per modal open)
  useEffect(() => {
    if (isOpen && selectedProject && !biddingToEdit && !hasAutoFilledRef.current) {
      hasAutoFilledRef.current = true;
      setProjectId(selectedProject.id);
      
      // Check for existing bidding sheets with same name and add version
      const checkAndSetName = async () => {
        const baseName = `${selectedProject.name} ${t('bidding.bidSuffix')}`;
        
        try {
          const token = localStorage.getItem('spellbook_token');
          const url = `${API_CONFIG.API_URL}/bidding/project/${selectedProject.id}`;
          
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            
            const existingBiddings = data.biddingSheets || data.biddings || [];
            
            // Find all biddings with similar names (with or without version)
            const versionRegex = /\s+v(\d+)$/;
            const baseNameWithoutVersion = baseName.replace(versionRegex, '');
            
            const similarNames = existingBiddings
              .map((b: any) => b.name)
              .filter((n: string) => {
                if (!n) return false;
                // Remove version from name to compare base
                const nameWithoutVersion = n.replace(versionRegex, '');
                return nameWithoutVersion === baseNameWithoutVersion || n === baseName;
              });
            
            if (similarNames.length === 0) {
              // No existing biddings, check if base name already has version
              const match = baseName.match(versionRegex);
              if (match) {
                // Base name has version (e.g., "Project Bid v01"), use it
                setName(baseName);
              } else {
                // No version in base name, add v01
                const newName = `${baseName} v01`;
                setName(newName);
              }
            } else {
              // Find the highest version number
              let maxVersion = 0;
              
              similarNames.forEach((n: string) => {
                const match = n.match(versionRegex);
                if (match) {
                  const version = parseInt(match[1]);
                  maxVersion = Math.max(maxVersion, version);
                } else {
                  // Name without version counts as v0
                  maxVersion = Math.max(maxVersion, 0);
                }
              });
              
              const newVersion = String(maxVersion + 1).padStart(2, '0');
              const newName = `${baseNameWithoutVersion} v${newVersion}`;
              setName(newName);
            }
          } else {
            setName(`${baseName} v01`);
          }
        } catch (error) {
          console.error('Error checking existing biddings:', error);
          setName(`${baseName} v01`);
        }
      };
      
      checkAndSetName();
    }
  }, [isOpen, selectedProject, biddingToEdit]); // Removed 't' from dependencies

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setProjectId('');
      setVideoFile(null);
      setShotCount(10);
      setSelectedSpecializations([]);
      setError(null);
      setUploadProgress(0);
      setShowSpecsWarning(false);
      hasAutoFilledRef.current = false; // Reset auto-fill flag when modal closes
    }
  }, [isOpen]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
      const validExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.prores'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
        setVideoFile(file);
        setError(null);
      } else {
        setError(t('bidding.invalidFileType'));
      }
    }
  }, [t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setVideoFile(file);
      setError(null);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError(t('bidding.nameRequired'));
      return;
    }

    if (!projectId) {
      setError(t('bidding.projectRequired'));
      return;
    }

    if (mode === 'video' && !videoFile && !biddingToEdit) {
      setError(t('bidding.videoRequired'));
      return;
    }

    if (mode === 'manual' && shotCount < 1) {
      setError(t('bidding.shotCountRequired'));
      return;
    }

    if (selectedSpecializations.length === 0) {
      setShowSpecsWarning(true);
      return;
    }

    void performCreate();
  };

  const performCreate = async () => {
    setShowSpecsWarning(false);

    // Check for duplicate name
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/project/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const existingBiddings = data.biddingSheets || [];
        const duplicate = existingBiddings.find((b: any) => 
          b.id !== biddingToEdit?.id && b.name.toLowerCase() === name.trim().toLowerCase()
        );
        
        if (duplicate) {
          setError(t('bidding.duplicateNameError'));
          return;
        }
      }
    } catch (error) {
      console.error('Error checking duplicate name:', error);
    }

    setIsUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Step 1: Create bidding sheet
      const createResponse = await fetch(`${API_CONFIG.API_URL}/bidding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          projectId,
          currency,
          valueColumnMode: 'hours',
        })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create bidding sheet');
      }

      const { id: biddingSheetId } = await createResponse.json();

      // Persist pipeline columns (empty array allowed if user confirmed warning)
      {
        const specsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/specializations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ specializations: selectedSpecializations })
        });
        if (!specsResponse.ok) {
          console.warn('Failed to save specializations for bidding sheet');
        }
      }

      if (mode === 'video' && videoFile) {
        // Video mode: Upload video and detect scenes
        const formData = new FormData();
        formData.append('video', videoFile);

        const uploadResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload video');
        }

        setUploadProgress(100);

        // Step 3: Start scene detection (async - returns immediately)
        setIsProcessing(true);
        const detectResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/detect-scenes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            threshold: 15.0,  // Lower threshold = more sensitive (more scenes detected)
            ...(parsedShotFilter.length > 0
              ? {
                  shotFilter: parsedShotFilter,
                  insertMissingShots: insertMissingShots,
                }
              : {})
          })
        });

        if (!detectResponse.ok && detectResponse.status !== 202) {
          throw new Error('Failed to start scene detection');
        }

        // Poll for scene detection completion
        const pollInterval = 3000; // 3 seconds
        const maxPolls = 400; // 20 minutes max
        let pollCount = 0;
        const usedShotFilter = parsedShotFilter.length > 0;
        
        const pollForCompletion = async (): Promise<boolean> => {
          const statusResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/video-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (!statusResponse.ok) {
            throw new Error('Failed to check processing status');
          }
          
          const statusData = await statusResponse.json();
          
          // Update progress thumbnails during processing
          if (statusData.progressThumbnails && statusData.progressThumbnails.length > 0) {
            setProgressThumbnails(statusData.progressThumbnails);
          }
          if (statusData.progressShotNames) {
            setProgressShotNames(statusData.progressShotNames);
          }
          if (statusData.detectionProgress) {
            setDetectionProgress(statusData.detectionProgress);
          }
          
          if (statusData.videoStatus === 'completed') {
            setDetectionProgress((prev) => ({ ...(prev || {}), percent: 100 }));
            return true;
          } else if (statusData.videoStatus === 'failed') {
            if (usedShotFilter && (statusData.filterNoMatches || statusData.shotCount === 0)) {
              throw new Error(t('bidding.shotFilterNoMatches'));
            }
            const detail = statusData.detectionError
              ? String(statusData.detectionError).slice(0, 300)
              : '';
            throw new Error(
              detail
                ? `${t('bidding.detectionFailed') || 'Scene detection failed'}: ${detail}`
                : (t('bidding.detectionFailed') || 'Scene detection failed on server')
            );
          }
          
          return false;
        };
        
        // Wait for completion with polling
        while (pollCount < maxPolls) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          pollCount++;
          
          const isCompleted = await pollForCompletion();
          if (isCompleted) {
            break;
          }
        }
        
        if (pollCount >= maxPolls) {
          throw new Error('Scene detection timed out');
        }
      } else if (mode === 'manual') {
        // Manual mode: Create empty shots
        setIsProcessing(true);
        
        for (let i = 1; i <= shotCount; i++) {
          const shotResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              shotNumber: i,
              comment: ''
            })
          });
          
          if (!shotResponse.ok) {
            const errorText = await shotResponse.text();
            console.error(`Failed to create shot ${i}:`, errorText);
            throw new Error(`Failed to create shot ${i}: ${shotResponse.status} ${shotResponse.statusText}`);
          }
          
        }
      }

      // Success
      onSave({ id: biddingSheetId, name, projectId, currency });
      handleClose();
      
    } catch (err: any) {
      console.error('Error creating bidding:', err);
      setError(err.message || t('bidding.createError'));
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setName('');
    setProjectId('');
    setVideoFile(null);
    setIsUploading(false);
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
    setDragActive(false);
    setProgressThumbnails([]);
    setProgressShotNames([]);
    setDetectionProgress(null);
    setShotFilterText('');
    setInsertMissingShots(false);
    setSelectedSpecializations([]);
    setShowSpecsWarning(false);
    onClose();
  };

  const showProcessingView = mode === 'video' && (isUploading || isProcessing);

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        biddingToEdit 
          ? t('bidding.editBidding') 
          : mode === 'video' 
            ? t('bidding.createBiddingFromVideo') 
            : t('bidding.createBiddingManual')
      }
      size="xl"
    >
      <div className="space-y-3">
        {showProcessingView ? (
          <>
            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">{t('bidding.uploading')}</span>
                  <span className="text-indigo-400">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Processing Indicator with Progressive Thumbnails */}
            {isProcessing && (
              <div className="space-y-3 p-4 bg-indigo-500/10 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500" />
                  <span className="text-indigo-400">
                    {t('bidding.detectingScenes')}
                    {progressThumbnails.length > 0 && (
                      <span className="text-indigo-300 ml-2">
                        ({progressThumbnails.length} {t('bidding.scenesDetected')}
                        {progressShotNames.some(Boolean) && (
                          <> · {t('bidding.recognizingNames')}</>
                        )}
                        )
                      </span>
                    )}
                  </span>
                </div>

                {/* Detection progress: avg(playhead, filter list) from backend */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">
                      {detectionProgress?.phase === 'gap' || detectionProgress?.phase === 'orphan'
                        ? t('bidding.detectionPhase2')
                        : detectionProgress &&
                            typeof detectionProgress.filterTotal === 'number' &&
                            detectionProgress.filterTotal > 0
                          ? `${detectionProgress.matchedCount ?? 0}/${detectionProgress.filterTotal} ${t('bidding.detectionShots')} · ${Math.round((detectionProgress.playhead ?? 0) * 100)}% ${t('bidding.detectionPlayhead')}`
                          : t('bidding.detectionProgress')}
                    </span>
                    <span className="text-indigo-400 font-medium">
                      {detectionProgress?.percent ?? 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, detectionProgress?.percent ?? 0)}%` }}
                    />
                  </div>
                </div>

                {progressThumbnails.length > 0 && (
                  <div className="relative overflow-hidden rounded-lg bg-gray-900/50 p-2">
                    <div className="flex flex-wrap gap-1">
                      {progressThumbnails.map((thumbnail, index) => {
                        const ocrName = progressShotNames[index];
                        return (
                          <div
                            key={thumbnail}
                            className="relative overflow-hidden rounded animate-fadeIn flex flex-col"
                            style={{
                              animationDelay: `${index * 50}ms`,
                              flex: '0 0 auto',
                              maxWidth: '72px'
                            }}
                          >
                            <img
                              src={`${API_CONFIG.API_URL.replace('/api/v1', '')}${thumbnail}`}
                              alt={ocrName || `Shot ${index + 1}`}
                              className="h-12 w-auto object-cover rounded opacity-0 animate-thumbnailAppear"
                              style={{ animationDelay: `${index * 50}ms` }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <div className="px-0.5 pt-0.5 text-center leading-tight">
                              <span className="text-[8px] text-white/90 font-medium block truncate">
                                {ocrName || (index + 1)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Cancel only during upload/detection */}
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
        {/* Name + Project side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('bidding.name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              placeholder={t('bidding.namePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('bidding.project')} *
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
            >
              <option value="">{t('bidding.selectProject')}</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Specializations Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {t('bidding.selectSpecializations')} <span className="text-red-400">*</span>
          </label>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-8 gap-1.5">
            {PIPELINE_SPECIALIZATIONS.map((spec) => (
              <label
                key={spec}
                className={`flex items-center justify-center px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors ${
                  selectedSpecializations.includes(spec)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedSpecializations.includes(spec)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedSpecializations([...selectedSpecializations, spec]);
                    } else {
                      setSelectedSpecializations(selectedSpecializations.filter(s => s !== spec));
                    }
                  }}
                  className="sr-only"
                />
                <span className="text-sm font-medium whitespace-nowrap">{spec}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t('bidding.specializationsHint')}
          </p>
        </div>

        {/* Video Upload Area (Video Mode) */}
        {!biddingToEdit && mode === 'video' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('bidding.video')} *
            </label>
            <div
              className={`relative border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
                dragActive
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {videoFile ? (
                <div className="space-y-3">
                  <div className="p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/50">
                    <ICONS.Video className="w-16 h-16 mx-auto text-indigo-400 mb-3" />
                    <p className="text-white font-semibold text-lg mb-1">{videoFile.name}</p>
                    <p className="text-sm text-indigo-400">
                      {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setVideoFile(null)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <ICONS.Trash className="w-4 h-4" />
                    <span>{t('common.remove')}</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <ICONS.Upload className="w-16 h-16 mx-auto text-gray-600" />
                  <div className="space-y-2">
                    <p className="text-gray-300 text-lg font-medium">
                      {t('bidding.dragDropVideo')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('bidding.supportedFormats')}: <span className="font-medium text-gray-400">MP4, MOV, ProRes, AVI, MKV</span>
                    </p>
                  </div>
                  <label className="inline-block">
                    <span className="inline-flex items-center space-x-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg cursor-pointer transition-all hover:shadow-lg hover:scale-105">
                      <ICONS.Upload className="w-5 h-5" />
                      <span className="font-medium">{t('bidding.selectFile')}</span>
                    </span>
                    <input
                      type="file"
                      accept="video/*,.prores"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Optional shot filter from pasted column (Video Mode) */}
        {!biddingToEdit && mode === 'video' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('bidding.shotFilter')}
              <span className="text-gray-500 font-normal ml-1">({t('common.optional')})</span>
            </label>
            <textarea
              value={shotFilterText}
              onChange={(e) => setShotFilterText(e.target.value)}
              onBlur={() => {
                // Normalize newlines/tabs to comma-separated for readability
                if (shotFilterText.trim()) {
                  const parsed = parseShotFilter(shotFilterText);
                  if (parsed.length > 0) {
                    setShotFilterText(parsed.join(', '));
                  }
                }
              }}
              rows={2}
              className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 font-mono text-sm resize-y overflow-y-auto"
              placeholder={t('bidding.shotFilterPlaceholder')}
            />
            <p className="text-xs text-gray-500 mt-1 truncate" title={t('bidding.shotFilterHint')}>
              {t('bidding.shotFilterHint')}
              {parsedShotFilter.length > 0 && (
                <span className="text-indigo-400 ml-1">
                  ({parsedShotFilter.length} {t('bidding.shotFilterCount')})
                </span>
              )}
            </p>
            {parsedShotFilter.length > 0 && (
              <label
                className="mt-2 flex items-start gap-2 text-sm text-gray-300 cursor-pointer"
                title={t('bidding.insertMissingShotsHint')}
              >
                <input
                  type="checkbox"
                  checked={insertMissingShots}
                  onChange={(e) => setInsertMissingShots(e.target.checked)}
                  className="mt-0.5 rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                />
                <span>
                  {t('bidding.insertMissingShots')}
                  <span className="block text-xs text-gray-500 font-normal">
                    {t('bidding.insertMissingShotsHint')}
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* Shot Count Input (Manual Mode) */}
        {!biddingToEdit && mode === 'manual' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('bidding.shotCount')} *
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={shotCount}
              onChange={(e) => setShotCount(parseInt(e.target.value) || 1)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              placeholder={t('bidding.shotCountPlaceholder')}
              disabled={isUploading || isProcessing}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bidding.shotCountHint')}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Info Message */}
        {!biddingToEdit && (
          <div className="p-3 bg-blue-500/10 border border-blue-500/50 rounded-lg text-blue-400 text-sm">
            <ICONS.Info className="inline w-4 h-4 mr-2" />
            {mode === 'video' ? t('bidding.sceneDetectionInfo') : t('bidding.manualModeInfo')}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            disabled={isUploading || isProcessing}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              isUploading || 
              isProcessing || 
              !name.trim() || 
              !projectId || 
              (mode === 'video' && !videoFile && !biddingToEdit) ||
              (mode === 'manual' && shotCount < 1)
            }
          >
            {isUploading || isProcessing ? t('common.processing') : t('common.create')}
          </button>
        </div>
          </>
        )}
      </div>
    </Modal>

    <ConfirmationModal
      isOpen={showSpecsWarning}
      onClose={() => setShowSpecsWarning(false)}
      onConfirm={() => { void performCreate(); }}
      type="warning"
      title={t('bidding.specializationsRequiredTitle')}
      message={t('bidding.specializationsRequiredMessage')}
      confirmText={t('bidding.continueWithoutSpecializations')}
      cancelText={t('bidding.goSelectSpecializations')}
    />
    </>
  );
};

export default BiddingModal;

